import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Divider,
  Flex,
  Skeleton,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBooks,
  faBoxArchive,
  faFileLines,
  faMinus,
  faPlus,
  faUser,
  faCalendarDay
} from '@fortawesome/pro-regular-svg-icons';
import {
  fetchAuthorProfileData,
  fetchUserProfileData,
  fetchMyRecentActivity,
  IMyRecentActivityResponse,
  IRecentActivityJournal,
  IRoleWiseActivity,
  IActivityItem,
  fetchUserSubmissionsTotals
} from '../../api/queries';
import dateFormat from 'dateformat';
import { ACTIVITY_TYPE_INFO } from '../../../../domain/models/activity-log';
import { RichTextDisplay } from '../../components/Submission/GenericFieldComponents/RichText/RichTextDisplay';
import { getMyActivityTitle } from './myActivityHandlers';
import { allActivityLogTypes } from '@publishing-engine/domain/models';
import { clientAuthRealm } from '../../utils/env';
import { useKeycloak } from '../../components/AuthHOC';
import JournalIndex from '../JournalIndex';
import { ButtonText } from '@morressier/alembic';
import { useLocation } from 'react-router-dom';
import { USER_ACTIVITY_TOTALS_QUERY_KEY } from '../../constants';

interface IActionLink {
  label: string;
  href?: string;
  onClick?: () => void;
}

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser: { id },
    authRealm
  } = useKeycloak();

  const { data: userData } = useQuery({
    queryKey: ['user-profile-data'],
    queryFn: () => fetchUserProfileData()
  });

  const { data: totalCount, isLoading: isLoadingTotalCount } = useQuery({
    queryKey: [USER_ACTIVITY_TOTALS_QUERY_KEY],
    queryFn: () =>
      fetchUserSubmissionsTotals({ authRealm: authRealm ?? '', userId: id })
  });

  const { data: authorData, isLoading: isLoadingAuthorData } = useQuery({
    queryKey: [
      'workspace-author-profile',
      { orgId: userData?.organizationId, userId: userData?.userId }
    ],
    queryFn: () =>
      fetchAuthorProfileData(
        userData?.organizationId ?? '',
        userData?.userId ?? ''
      ),
    enabled: !!userData?.organizationId && !!userData?.userId
  });

  const { data: recentActivityData, isLoading: isLoadingRecentActivity } =
    useQuery<IMyRecentActivityResponse>({
      queryKey: [
        'my-recent-activity',
        { authRealm: clientAuthRealm, userId: userData?.userId }
      ],
      queryFn: () =>
        fetchMyRecentActivity(clientAuthRealm ?? '', userData?.userId ?? ''),
      enabled: !!clientAuthRealm && !!userData?.userId
    });

  // Helper function to get link based on specific role
  const getActionLink = (
    role: string,
    isWorkspaceMember: boolean,
    journalId?: string,
    shortname?: string
  ): IActionLink[] => {
    // Show role-specific link for author and reviewer
    if (role.toLowerCase() === 'author') {
      return [
        {
          label: 'View all submissions',
          onClick: () =>
            navigate('/app/my-submissions?submissionType=my-submissions-active')
        }
      ];
    }
    if (role.toLowerCase() === 'reviewer') {
      return [
        {
          label: 'View all reviews',
          onClick: () => navigate('/app/reviews?reviewListType=active')
        }
      ];
    }

    // For all other roles (Staff, Observer, etc.), show workspace links if user is a workspace member
    if (isWorkspaceMember && journalId) {
      return [
        {
          label: 'View all submissions',
          onClick: () =>
            navigate(
              `/app/workspace/${shortname}/submissions?journalId=${journalId}`
            )
        },
        {
          label: 'View workflow',
          onClick: () =>
            navigate(
              `/app/workspace/${shortname}/workflow?journalId=${journalId}`
            )
        }
      ];
    }

    return [];
  };

  // Helper function to format author names
  const formatAuthors = (
    authors: Array<{ firstName?: string; lastName?: string }> | undefined
  ): string => {
    if (!authors?.length) return '';

    const formatName = (author: {
      firstName?: string;
      lastName?: string;
    }): string => `${author.firstName ?? ''} ${author.lastName ?? ''}`.trim();

    const names = authors.map(formatName);

    if (names.length === 1) return `${names[0]}`;
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
  };

  // Helper function to get human-readable activity label
  const getActivityLabel = (eventType: string, activityItem: IActivityItem) => {
    // Try to get title from myActivityHandlers first
    const activityTitle = getMyActivityTitle(
      eventType as allActivityLogTypes,
      activityItem.event as any
    );

    if (activityTitle) {
      return activityTitle;
    }

    // Fallback to ACTIVITY_TYPE_INFO
    return (
      ACTIVITY_TYPE_INFO[eventType as keyof typeof ACTIVITY_TYPE_INFO]?.label ||
      eventType
    );
  };

  // Helper function to handle activity card click
  const handleActivityCardClick = (
    role: string,
    activityItem: IActivityItem,
    submissionContextId: string,
    shortname?: string
  ) => {
    const roleLower = role.toLowerCase();

    if (roleLower === 'author') {
      navigate(
        `/app/my-submissions/${activityItem.submissionId}?submissionType=my-submissions-active`
      );
    } else if (roleLower === 'reviewer' && activityItem.event.reviewRequestId) {
      navigate(`/app/reviews/${activityItem.event.reviewRequestId}`, {
        state: { from: location.pathname + location.search }
      });
    } else {
      // For any other role (staff, observer, etc.), redirect to workspace submissions
      navigate(
        `/app/workspace/${shortname}/j/${submissionContextId}/submissions?selectedItem=${activityItem.submissionId}&activeTab=overview`,
        {
          state: { from: location.pathname + location.search }
        }
      );
    }
  };

  return (
    <>
      {isLoadingTotalCount || isLoadingAuthorData ? (
        <Box id="scrollable-wrapper" overflowY="auto" height="100%">
          <Box height="100%">
            <Box padding={4} minHeight="100%">
              <Box
                backgroundColor="#ffffff"
                p={4}
                width="100%"
                borderRadius="4px"
              >
                <Flex gap={2} flexDirection="column">
                  <Flex
                    gap={2}
                    alignItems={{ base: 'flex-start', md: 'center' }}
                    flexDirection={{ base: 'column', md: 'row' }}
                  >
                    <Skeleton height="24px" width="200px" />
                    <Skeleton height="20px" width="60px" borderRadius="4px" />
                    <Skeleton height="20px" width="60px" borderRadius="4px" />
                  </Flex>
                  <Flex gap={2} alignItems="center" flexWrap="wrap">
                    <Skeleton height="16px" width="16px" />
                    <Skeleton
                      height="16px"
                      width={{ base: '100%', md: '300px' }}
                      maxWidth="300px"
                    />
                  </Flex>
                </Flex>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : totalCount &&
        totalCount?.totalDraftSubmissions === 0 &&
        totalCount?.totalSubmissions === 0 &&
        totalCount?.totalActiveReviews === 0 &&
        totalCount?.totalCompletedReviews === 0 &&
        !authorData?.isWorkspaceMember ? (
        <JournalIndex />
      ) : (
        <Box id="scrollable-wrapper" overflowY="auto" height="100%">
          <Box height="100%">
            <Box padding={4} minHeight="100%">
              <Box
                backgroundColor="#ffffff"
                p={3}
                width="100%"
                borderRadius="4px"
              >
                {isLoadingAuthorData ? (
                  <Flex gap={2} flexDirection="column">
                    <Flex
                      gap={2}
                      alignItems={{ base: 'flex-start', md: 'center' }}
                      flexDirection={{ base: 'column', md: 'row' }}
                    >
                      <Skeleton height="24px" width="200px" />
                      <Skeleton height="20px" width="60px" borderRadius="4px" />
                      <Skeleton height="20px" width="60px" borderRadius="4px" />
                    </Flex>
                    <Flex gap={2} alignItems="center" flexWrap="wrap">
                      <Skeleton height="16px" width="16px" />
                      <Skeleton
                        height="16px"
                        width={{ base: '100%', md: '300px' }}
                        maxWidth="300px"
                      />
                    </Flex>
                  </Flex>
                ) : (
                  <Flex gap={2} flexDirection="column">
                    <Flex gap={2} textStyle="headingItem">
                      <Text>
                        {authorData?.user?.firstName ?? ''}{' '}
                        {authorData?.user?.middleName ?? ''}{' '}
                        {authorData?.user?.lastName ?? ''}
                      </Text>
                    </Flex>
                    {authorData?.journals && authorData.journals.length > 0 && (
                      <Flex
                        gap={2}
                        alignItems="flex-start"
                        flexDirection="column"
                      >
                        {authorData.journals.map(journal => (
                          <Flex
                            key={journal.id}
                            gap={2}
                            alignItems={{ base: 'flex-start', md: 'center' }}
                            flexWrap="wrap"
                          >
                            <FontAwesomeIcon icon={faBooks} />
                            <Text textStyle="textBody">{journal.name}</Text>
                            {journal.roles?.map(role => (
                              <Badge
                                key={`${journal.id}-${role}`}
                                textStyle="textBody"
                                textTransform="capitalize"
                              >
                                {role}
                              </Badge>
                            ))}
                          </Flex>
                        ))}
                      </Flex>
                    )}
                  </Flex>
                )}
              </Box>
              <Flex
                flexDirection="column"
                width="100%"
                alignItems="flex-start"
                gap={3}
              >
                <Box width="100%">
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    mt={4}
                    mb={3}
                  >
                    <Text textStyle="headingGroup">Recent Activity</Text>
                  </Flex>
                </Box>
              </Flex>
              {isLoadingRecentActivity ? (
                <Box
                  border="0.5px solid #D0D5DD"
                  borderRadius="4px"
                  backgroundColor="#ffffff"
                  padding={4}
                >
                  <Flex flexDirection="column" gap={4}>
                    <Flex gap={2} alignItems="center">
                      <Skeleton height="16px" width="16px" />
                      <Skeleton height="20px" width="200px" />
                    </Flex>
                    <Skeleton height="1px" width="100%" />
                    <Flex flexDirection="column" gap={3} padding={2}>
                      {[1, 2, 3].map(index => (
                        <Box key={index}>
                          <Skeleton
                            height="16px"
                            width="150px"
                            marginBottom={2}
                          />
                          <Skeleton
                            height="20px"
                            width="100%"
                            marginBottom={2}
                          />
                          <Flex gap={4} marginBottom={2}>
                            <Skeleton height="16px" width="100px" />
                            <Skeleton height="16px" width="120px" />
                            <Skeleton height="16px" width="150px" />
                          </Flex>
                          {index < 3 && (
                            <Divider borderColor="#E5E7EB" marginTop={3} />
                          )}
                        </Box>
                      ))}
                    </Flex>
                  </Flex>
                </Box>
              ) : !recentActivityData?.recentActivity ||
                recentActivityData.recentActivity.length === 0 ? (
                <Box
                  border="0.5px solid #D0D5DD"
                  borderRadius="4px"
                  backgroundColor="#ffffff"
                  padding={{ base: '40px 16px', md: '80px 24px' }}
                >
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={3}
                  >
                    <FontAwesomeIcon icon={faBoxArchive} fontSize={32} />
                    <Text textStyle="headingSection">No Recent Activity</Text>
                    <Text textStyle="textBody" textAlign="center">
                      Here, you will find submissions that are prioritized for
                      your interaction, as well as those you have recently
                      engaged with.
                    </Text>
                  </Flex>
                </Box>
              ) : (
                <Accordion
                  allowToggle
                  defaultIndex={[0]}
                  border="0.5px solid #D0D5DD"
                  borderRadius="4px"
                  backgroundColor="#ffffff"
                >
                  {recentActivityData?.recentActivity?.map(
                    (journalActivity: IRecentActivityJournal) => {
                      return (
                        <AccordionItem key={journalActivity.journal.id}>
                          {({ isExpanded }) => (
                            <>
                              <AccordionButton padding={3}>
                                <Flex
                                  gap={2}
                                  textAlign="left"
                                  alignItems="center"
                                  minWidth={0}
                                >
                                  <FontAwesomeIcon icon={faBooks} />
                                  <Text textStyle="textBody" noOfLines={2}>
                                    {journalActivity.journal.name}
                                  </Text>
                                </Flex>
                                <Box ml="auto">
                                  {isExpanded ? (
                                    <FontAwesomeIcon icon={faMinus} />
                                  ) : (
                                    <FontAwesomeIcon icon={faPlus} />
                                  )}
                                </Box>
                              </AccordionButton>
                              <AccordionPanel
                                padding={0}
                                borderTop="0.5px solid #D0D5DD"
                              >
                                <Box>
                                  {journalActivity.roleWiseActivity?.map(
                                    (roleActivity: IRoleWiseActivity) => {
                                      // Get action links for this specific role, using the global isWorkspaceMember flag
                                      const actionLinks = getActionLink(
                                        roleActivity.role,
                                        recentActivityData?.isWorkspaceMember ??
                                          false,
                                        journalActivity.journal.id,
                                        recentActivityData?.workspace?.shortname
                                      );

                                      return (
                                        <Box key={roleActivity.role}>
                                          <Flex
                                            backgroundColor="#F2F4F7"
                                            justifyContent="space-between"
                                            alignItems={{
                                              base: 'flex-start',
                                              md: 'center'
                                            }}
                                            flexDirection={{
                                              base: 'column',
                                              md: 'row'
                                            }}
                                            gap={2}
                                            padding={3}
                                          >
                                            <Flex alignItems="center" gap={2}>
                                              <Text
                                                textStyle="headingLine"
                                                textTransform="capitalize"
                                              >
                                                {roleActivity.role}
                                              </Text>
                                            </Flex>
                                            {actionLinks.length > 0 && (
                                              <Flex
                                                alignItems="center"
                                                flexWrap="wrap"
                                                gap={2}
                                              >
                                                {actionLinks.map(
                                                  (
                                                    link: IActionLink,
                                                    index: number
                                                  ) => (
                                                    <Flex
                                                      alignItems="center"
                                                      key={link.label}
                                                    >
                                                      <ButtonText
                                                        justifyContent="flex-start"
                                                        onClick={link.onClick}
                                                      >
                                                        <Text>
                                                          {link.label}
                                                        </Text>
                                                      </ButtonText>
                                                      {index <
                                                        actionLinks.length -
                                                          1 && (
                                                        <Divider
                                                          orientation="vertical"
                                                          marginInline={2}
                                                          borderColor="#D0D5DD"
                                                          height="20px"
                                                          display={{
                                                            base: 'none',
                                                            md: 'inline-block'
                                                          }}
                                                        />
                                                      )}
                                                    </Flex>
                                                  )
                                                )}
                                              </Flex>
                                            )}
                                          </Flex>

                                          {!roleActivity.activity ||
                                          roleActivity.activity.length === 0 ? (
                                            <Flex
                                              padding={4}
                                              justifyContent="center"
                                              alignItems="center"
                                              mb={4}
                                            >
                                              <Text
                                                textStyle="textBody"
                                                textAlign="center"
                                              >
                                                Currently, there are no priority
                                                submissions for this journal,
                                                nor have you recently engaged
                                                with any submissions.
                                              </Text>
                                            </Flex>
                                          ) : (
                                            <Flex flexDirection="column">
                                              {roleActivity.activity.map(
                                                (
                                                  activityItem: IActivityItem,
                                                  index: number
                                                ) => {
                                                  const isDeclinedReview =
                                                    activityItem.event
                                                      .subtype ===
                                                      'declineRequest';
                                                  return (
                                                    <Tooltip
                                                      hasArrow
                                                      borderRadius={4}
                                                      placement="top"
                                                      label={
                                                        isDeclinedReview
                                                          ? undefined
                                                          : roleActivity.role.toLowerCase() ===
                                                            'reviewer'
                                                          ? 'View Review'
                                                          : 'View Submission'
                                                      }
                                                    >
                                                      <Box
                                                        key={activityItem._id}
                                                        borderBottom={
                                                          index <
                                                          roleActivity.activity
                                                            .length -
                                                            1
                                                            ? '1px solid #E5E7EB'
                                                            : 'none'
                                                        }
                                                        cursor={
                                                          isDeclinedReview
                                                            ? 'default'
                                                            : 'pointer'
                                                        }
                                                        onClick={() => {
                                                          if (!isDeclinedReview) {
                                                            handleActivityCardClick(
                                                              roleActivity.role,
                                                              activityItem,
                                                              journalActivity
                                                                .journal.id,
                                                              recentActivityData
                                                                ?.workspace
                                                                ?.shortname
                                                            );
                                                          }
                                                        }}
                                                        _hover={
                                                          isDeclinedReview
                                                            ? undefined
                                                            : {
                                                                backgroundColor:
                                                                  '#F7FAFC'
                                                              }
                                                        }
                                                        transition="background-color 0.2s"
                                                        padding={3}
                                                      >
                                                        <Flex
                                                          gap={2}
                                                          alignItems="center"
                                                          flexWrap="wrap"
                                                        >
                                                          <Text textStyle="headingLine">
                                                            #{' '}
                                                            {
                                                              activityItem.manuscriptId
                                                            }
                                                          </Text>
                                                          <Divider
                                                            orientation="vertical"
                                                            borderColor="#D0D5DD"
                                                            height="20px"
                                                            display="inline-block"
                                                          />
                                                          <Text
                                                            textStyle="textBody"
                                                            color={
                                                              isDeclinedReview
                                                                ? 'black'
                                                                : '#2043DF'
                                                            }
                                                            wordBreak="break-word"
                                                          >
                                                            {getActivityLabel(
                                                              activityItem.event
                                                                .type,
                                                              activityItem
                                                            )}
                                                          </Text>
                                                        </Flex>
                                                        <Text
                                                          textStyle="textBody"
                                                          marginBottom={1}
                                                        >
                                                          <RichTextDisplay
                                                            value={
                                                              activityItem.submissionTitle
                                                            }
                                                          />
                                                        </Text>
                                                        <Flex
                                                          gap={{ base: 2, md: 4 }}
                                                          textStyle="textContext"
                                                          flexWrap="wrap"
                                                          alignItems={{
                                                            base: 'flex-start',
                                                            md: 'center'
                                                          }}
                                                          flexDirection={{
                                                            base: 'column',
                                                            sm: 'row'
                                                          }}
                                                        >
                                                          <Flex
                                                            alignItems="center"
                                                            gap={2}
                                                          >
                                                            <FontAwesomeIcon
                                                              icon={
                                                                faCalendarDay
                                                              }
                                                            />
                                                            <Text>
                                                              {dateFormat(
                                                                new Date(
                                                                  activityItem.event.date
                                                                ),
                                                                'mmm d, yyyy'
                                                              )}
                                                            </Text>
                                                          </Flex>
                                                          <Flex
                                                            alignItems="center"
                                                            gap={2}
                                                          >
                                                            <FontAwesomeIcon
                                                              icon={faFileLines}
                                                            />
                                                            <Text>
                                                              {
                                                                activityItem.submissionType
                                                              }
                                                            </Text>
                                                          </Flex>
                                                          <Flex
                                                            alignItems="center"
                                                            gap={2}
                                                          >
                                                            <FontAwesomeIcon
                                                              icon={faUser}
                                                            />
                                                            <Text>
                                                              {formatAuthors(
                                                                activityItem.authors
                                                              )}
                                                            </Text>
                                                          </Flex>
                                                        </Flex>
                                                      </Box>
                                                    </Tooltip>
                                                  );
                                                }
                                              )}
                                            </Flex>
                                          )}
                                        </Box>
                                      );
                                    }
                                  )}
                                </Box>
                              </AccordionPanel>
                            </>
                          )}
                        </AccordionItem>
                      );
                    }
                  )}
                </Accordion>
              )}
            </Box>

            <Footer />
          </Box>
        </Box>
      )}
    </>
  );
};

export default HomePage;
